export function normalizeCurrencyCode(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = value.trim().toUpperCase();

  return /^[A-Z]{3}$/.test(normalized) ? normalized : undefined;
}

export function resolveCurrencyCode(
  ...sources: Array<string | null | undefined | unknown>
): string | undefined {
  for (const source of sources) {
    const normalized = normalizeCurrencyCode(source);

    if (normalized) {
      return normalized;
    }
  }

  return undefined;
}

type FormatMessageFn = (
  descriptor: {
    id: string;
    defaultMessage?: string;
  },
  values?: Record<string, unknown>
) => string;

export function resolveCurrencyDisplayLabel(options: {
  currencyLabel?: string | null;
  currencyCode?: string | null;
  formatMessage?: FormatMessageFn;
}): string | undefined {
  const explicitLabel = options.currencyLabel?.trim();

  if (explicitLabel) {
    return explicitLabel;
  }

  const code = normalizeCurrencyCode(options.currencyCode);

  if (!code) {
    return undefined;
  }

  if (options.formatMessage) {
    return options.formatMessage({ id: code.toLowerCase(), defaultMessage: code });
  }

  return code;
}

export function formatPaymentAmountDisplay(
  amount: number,
  options: {
    currency?: string | null;
    currencyLabel?: string | null;
    locale?: string;
    formatMessage?: FormatMessageFn;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): string {
  const locale = options.locale ?? 'en-US';
  const formattedAmount = Number(amount).toLocaleString(locale, {
    minimumFractionDigits: options.minimumFractionDigits ?? 0,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  });

  const label = resolveCurrencyDisplayLabel({
    currencyLabel: options.currencyLabel,
    currencyCode: options.currency,
    formatMessage: options.formatMessage,
  });

  if (!label) {
    return formattedAmount;
  }

  return `${formattedAmount} ${label}`;
}
